import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { useI18n } from '../contexts/I18nContext';
import '../styles/components/CodeEditorModal.css';

interface CodeEditorModalProps {
  isOpen: boolean;
  code: string;
  language: string;
  onSave: (code: string, language: string) => void;
  onClose: () => void;
}

const CodeEditorModal: React.FC<CodeEditorModalProps> = ({
  isOpen,
  code,
  language,
  onSave,
  onClose,
}) => {
  const { t } = useI18n();
  const [editCode, setEditCode] = useState(code);
  const [editLanguage, setEditLanguage] = useState(language);

  useEffect(() => {
    setEditCode(code);
    setEditLanguage(language);
  }, [code, language, isOpen]);

  const handleSave = () => {
    onSave(editCode, editLanguage);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="code-editor-modal-overlay" onClick={onClose}>
      <div
        className="code-editor-modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="code-editor-modal-header">
          <div className="code-editor-modal-title">
            <span>{t.modals.codeEditor.title}</span>
            <select
              value={editLanguage}
              onChange={(e) => setEditLanguage(e.target.value)}
              className="language-selector"
            >
              <option value="javascript">JavaScript</option>
              <option value="typescript">TypeScript</option>
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="cpp">C++</option>
              <option value="c">C</option>
              <option value="csharp">C#</option>
              <option value="go">Go</option>
              <option value="rust">Rust</option>
              <option value="php">PHP</option>
              <option value="ruby">Ruby</option>
              <option value="html">HTML</option>
              <option value="css">CSS</option>
              <option value="json">JSON</option>
              <option value="markdown">Markdown</option>
              <option value="sql">SQL</option>
              <option value="shell">Shell</option>
            </select>
          </div>
          <div className="code-editor-modal-actions">
            <button onClick={onClose} className="btn-secondary">
              {t.modals.codeEditor.cancel}
            </button>
            <button onClick={handleSave} className="btn-primary">
              {t.modals.codeEditor.save}
            </button>
          </div>
        </div>
        <div className="code-editor-modal-body">
          <Editor
            height="100%"
            language={editLanguage}
            value={editCode}
            onChange={(value) => setEditCode(value || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CodeEditorModal;
